import React from 'react';
import { X } from 'lucide-react';
import { useConsoleStore } from '../../store/consoleStore';

interface Props {
  onClose: () => void;
}

export const ConsoleSettings: React.FC<Props> = ({ onClose }) => {
  const settings = useConsoleStore((state) => state.settings);
  const updateSettings = useConsoleStore((state) => state.updateSettings);

  return (
    <div className="absolute top-10 right-2 w-72 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700">
        <h3 className="text-sm font-medium text-zinc-200">Console Settings</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-zinc-700 rounded text-zinc-400 hover:text-zinc-200"
        >
          <X size={14} />
        </button>
      </div>

      {/* Settings content */}
      <div className="p-3 space-y-4 max-h-80 overflow-y-auto">
        {/* Display section */}
        <div>
          <h4 className="text-xs font-medium text-zinc-400 uppercase mb-2">Display</h4>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showTimestamps}
                onChange={(e) => updateSettings({ showTimestamps: e.target.checked })}
                className="rounded bg-zinc-700 border-zinc-600 text-blue-500 focus:ring-blue-500"
              />
              Show timestamps
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showStackTraces}
                onChange={(e) => updateSettings({ showStackTraces: e.target.checked })}
                className="rounded bg-zinc-700 border-zinc-600 text-blue-500 focus:ring-blue-500"
              />
              Show stack traces
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showSource}
                onChange={(e) => updateSettings({ showSource: e.target.checked })}
                className="rounded bg-zinc-700 border-zinc-600 text-blue-500 focus:ring-blue-500"
              />
              Show source location
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.wrapLines}
                onChange={(e) => updateSettings({ wrapLines: e.target.checked })}
                className="rounded bg-zinc-700 border-zinc-600 text-blue-500 focus:ring-blue-500"
              />
              Wrap long lines
            </label>
          </div>
        </div>

        {/* Behavior section */}
        <div>
          <h4 className="text-xs font-medium text-zinc-400 uppercase mb-2">Behavior</h4>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.collapseIdentical}
                onChange={(e) => updateSettings({ collapseIdentical: e.target.checked })}
                className="rounded bg-zinc-700 border-zinc-600 text-blue-500 focus:ring-blue-500"
              />
              Collapse identical messages
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.clearOnPlay}
                onChange={(e) => updateSettings({ clearOnPlay: e.target.checked })}
                className="rounded bg-zinc-700 border-zinc-600 text-blue-500 focus:ring-blue-500"
              />
              Clear on play
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.preserveLog}
                onChange={(e) => updateSettings({ preserveLog: e.target.checked })}
                className="rounded bg-zinc-700 border-zinc-600 text-blue-500 focus:ring-blue-500"
              />
              Preserve log on navigation
            </label>
          </div>
        </div>

        {/* Limits section */}
        <div>
          <h4 className="text-xs font-medium text-zinc-400 uppercase mb-2">Limits</h4>
          <div className="space-y-3">
            <label className="block text-sm text-zinc-300">
              <span className="block mb-1">Max messages</span>
              <input
                type="number"
                value={settings.maxMessages}
                onChange={(e) => updateSettings({ maxMessages: parseInt(e.target.value) || 1000 })}
                min={100}
                max={10000}
                step={100}
                className="w-full px-2 py-1 text-sm bg-zinc-900 border border-zinc-600 rounded
                  focus:border-blue-500 focus:outline-none text-zinc-300"
              />
            </label>
            <label className="block text-sm text-zinc-300">
              <span className="block mb-1">Font size (px)</span>
              <input
                type="number"
                value={settings.fontSize}
                onChange={(e) => updateSettings({ fontSize: parseInt(e.target.value) || 12 })}
                min={10}
                max={18}
                step={1}
                className="w-full px-2 py-1 text-sm bg-zinc-900 border border-zinc-600 rounded
                  focus:border-blue-500 focus:outline-none text-zinc-300"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
